import { useEffect, useState, useMemo, useRef, memo, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Button, Row, Col, notification } from 'antd';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import './Home.scss';
import { t } from '../i18n/i18n';
import WorkspaceOptions from '../components/WorkspaceOptions/WorkspaceOptions';
import { getLocalStorageWorkspace } from '../utils/workspaceUtils';
import HomeUserProfile from '../components/HomeUserProfile/HomeUserProfile';
import HomePlanProfile from '../components/HomePlanProfile/HomePlanProfile';
import HomeUserProfileSkeleton from '../components/HomeUserProfile/HomeUserProfileSkeleton';
import HomePlanProfileSkeleton from '../components/HomePlanProfile/HomePlanProfileSkeleton';
import { getPlanByWorkspaceId, getPlanFeatures } from '../API/services/alkymersService';
import { setPlan, setPlanFeatures, setUser } from '../redux/actions/authActions';
import { disableForWorkspaceRole } from '../utils/workspaceRoleValidation';
import { PartnerWorkspaceRole } from '../constants/enums';
import UnreviewedEvaluations from '../components/UnreviewedEvaluations/UnreviewedEvaluations';
import UsageStatistics from '../components/UsageStatistics/UsageStatistics';
import NewsSection from '../components/NewsSection/NewsSection';
import FeaturedEvaluations from '../components/FeaturedEvaluations/FeaturedEvaluations';
import HomeDashboardSkeleton from '../components/HomeDashboardSkeleton/HomeDashboardSkeleton';
import PartnerOnboarding from '../components/PartnerOnboarding/PartnerOnboarding';
import EmptyEvaluationsState from '../components/EmptyEvaluationsState/EmptyEvaluationsState';
import { getOnboardingStatus } from '../API/services/onboardingService';
import { isOnboardingDismissed } from '../utils/onboardingStorage';
import { EventsGTM } from '../constants/events';
import { TrackEventGTM } from '../utils/filters/analyticsUtils';

const Home = () => {
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasEvaluations, setHasEvaluations] = useState(true);
  const hasSetLoadingPlanRef = useRef(false);
  const activeWorkspace = getLocalStorageWorkspace();
  const currentWorkspace = useSelector((state) => state.auth.currentWorkspace, shallowEqual);
  const plan = useSelector((state) => state.auth.plan, shallowEqual);
  const currentRole = useSelector((state) => state.auth.currentRole, shallowEqual);
  const user = useSelector((state) => state.auth.user, shallowEqual);
  const dispatch = useDispatch();
  const history = useHistory();

  const shouldSeePlanInformation = useMemo(() => {
    if (!currentWorkspace) return false;
    return !disableForWorkspaceRole(currentRole, [
      PartnerWorkspaceRole.OWNER,
      PartnerWorkspaceRole.ADMINISTRATOR,
    ]);
  }, [currentWorkspace, currentRole]);

  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (Object.keys(plan).length > 0 && loadingPlan) {
      setLoadingPlan(false);
    }
  }, [plan, loadingPlan]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!currentWorkspace || !user?.id) return;

    const checkOnboardingStatus = async () => {
      const isDismissed = isOnboardingDismissed(currentWorkspace, user.id);
      if (isDismissed) {
        setShowOnboarding(false);
        return;
      }

      try {
        const status = await getOnboardingStatus(currentWorkspace);
        if (isMountedRef.current) {
          setShowOnboarding(!status.allCompleted);
          setHasEvaluations(status.steps.evaluation);
        }
      } catch (error) {
        notification.error({
          message: t('ONBOARDING_ERROR_LOADING'),
          placement: 'topRight',
        });
        if (isMountedRef.current) {
          setShowOnboarding(false);
          setHasEvaluations(true);
        }
      }
    };

    checkOnboardingStatus();
  }, [currentWorkspace, user?.id]);

  const handleOnboardingDismiss = () => {
    setShowOnboarding(false);
  };

  const dashboardSection = useMemo(() => {
    if (!currentWorkspace) {
      return <HomeDashboardSkeleton />;
    }
    return (
      <section className="home-dashboard-container">
        <div className="evaluations-column">
          {hasEvaluations ? <UnreviewedEvaluations /> : <EmptyEvaluationsState />}
        </div>
        <aside className="stats-news-column">
          <div className="stats-container">
            {showOnboarding ? (
              <PartnerOnboarding
                workspaceId={currentWorkspace}
                userId={user?.id}
                onDismiss={handleOnboardingDismiss}
              />
            ) : (
              <UsageStatistics />
            )}
          </div>
          <div className="news-container">
            <NewsSection />
          </div>
        </aside>
      </section>
    );
  }, [currentWorkspace, hasEvaluations, showOnboarding]);

  return (
    <div className="home-container">
      <div className="home-display-content pb-5">
        <div className="home-top-section">
          <div className="d-block d-lg-flex bg-white justify-content-center py-2 w-100 justify-self-center rounded-top position-relative z-index-0">
            {user ? <HomeUserProfile /> : <HomeUserProfileSkeleton />}
            <div className="home-profile-separator" />
            {!user ? (
              <HomePlanProfileSkeleton />
            ) : shouldSeePlanInformation ? (
              loadingPlan ? (
                <HomePlanProfileSkeleton />
              ) : Object.values(plan).length ? (
                <HomePlanProfile />
              ) : null
            ) : null}
          </div>
          <WorkspaceOptions
            className="w-100 d-flex justify-content-center"
            hideFourth={activeWorkspace && activeWorkspace.createdAt !== activeWorkspace?.updatedAt}
            currentRole={currentRole}
            fromHome
          />
        </div>
        <FeaturedEvaluations />
        {dashboardSection}
      </div>
    </div>
  );
};

export default Home;
